"use client"

import { useState, useEffect } from "react"
import { GlowCard } from "@/components/crystality/glow-card"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Loader2, Eye, EyeOff, Check, Copy } from "lucide-react"
import { toast } from "sonner"
import { validateUsername } from "@/lib/utils"
import { trackConversion } from "@/lib/analytics"

interface AccountStepProps {
  email: string
  licenseKey: string
  orderId?: string
  onComplete: () => void
}

export function AccountStep({ email, licenseKey, orderId, onComplete }: AccountStepProps) {
  const [username, setUsername] = useState("")
  const [password, setPassword] = useState("")
  const [confirmPassword, setConfirmPassword] = useState("")
  const [showPassword, setShowPassword] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState("")
  const [copied, setCopied] = useState(false)
  const [usernameError, setUsernameError] = useState<string | null>(null)
  const [isExistingUser, setIsExistingUser] = useState(false)
  const [isCheckingUser, setIsCheckingUser] = useState(true)
  const [displayLicenseKey, setDisplayLicenseKey] = useState(licenseKey)
  const [isLoadingLicense, setIsLoadingLicense] = useState(false)
  const [redemptionData, setRedemptionData] = useState<any>(null)
  const [isCheckingRedemption, setIsCheckingRedemption] = useState(false)
  const [isRedeeming, setIsRedeeming] = useState(false)

  // Check if user is already logged in and has unredeemed license
  useEffect(() => {
    const checkExistingUser = async () => {
      try {
        const response = await fetch("/api/auth/me")
        if (response.ok) {
          const data = await response.json()
          if (data.success && data.user) {
            setIsExistingUser(true)
            
            // If we have orderId and email, check for unredeemed license
            if (orderId && email) {
              setIsCheckingRedemption(true)
              try {
                const redemptionResponse = await fetch(
                  `/api/get-started/check-redemption?orderId=${encodeURIComponent(orderId)}&email=${encodeURIComponent(email)}`
                )
                const redemptionData = await redemptionResponse.json()
                
                if (redemptionResponse.ok && redemptionData.success) {
                  if (redemptionData.hasUnredeemedLicense) {
                    setRedemptionData(redemptionData)
                  } else if (redemptionData.isRedeemedToThisUser) {
                    // Already redeemed, redirect to dashboard
                    toast.success("License already linked to your account!")
                    window.location.href = "/dashboard"
                    return
                  } else if (redemptionData.isRedeemedToOtherUser) {
                    setError("This license has already been redeemed to another account.")
                  }
                } else if (!redemptionResponse.ok) {
                  // Order not found or other error - fallback to reactivation UI
                }
              } catch (error) {
                console.error("Failed to check redemption:", error)
              } finally {
                setIsCheckingRedemption(false)
              }
            }
          }
        }
      } catch (error) {
        console.error("Failed to check user:", error)
      } finally {
        setIsCheckingUser(false)
      }
    }
    checkExistingUser()
  }, [orderId, email])

  // Fetch license key from database if not provided (for returning users)
  useEffect(() => {
    const fetchLicenseKey = async () => {
      // If we already have a license key, use it
      if (displayLicenseKey) {
        return
      }

      // If we have orderId and email, fetch from database
      if (orderId && email) {
        setIsLoadingLicense(true)
        try {
          const response = await fetch(
            `/api/get-started/retrieve-license?orderId=${encodeURIComponent(orderId)}&email=${encodeURIComponent(email)}`
          )
          if (response.ok) {
            const data = await response.json()
            if (data.success && data.licenseKey) {
              setDisplayLicenseKey(data.licenseKey)
              
              // If account already created, redirect to sign in
              if (data.accountCreated) {
                window.location.href = "/sign-in?message=account-already-exists"
                return
              }
            }
          }
        } catch (error) {
          console.error("Failed to fetch license key:", error)
        } finally {
          setIsLoadingLicense(false)
        }
      }
    }

    fetchLicenseKey()
  }, [orderId, email, displayLicenseKey])

  const handleReactivate = async () => {
    if (!orderId) {
      setError("Order ID not found. Please restart the checkout process.")
      return
    }

    setIsLoading(true)
    setError("")

    try {
      const response = await fetch("/api/get-started/reactivate", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, orderId }),
      })

      if (!response.ok) {
        const data = await response.json()
        throw new Error(data.error || "Failed to reactivate subscription")
      }

      await trackConversion("checkout_completed", {
        email,
        orderId,
        isReactivation: true,
      })

      await fetch("/api/get-started/progress", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, step: "success", data: {} }),
      })

      onComplete()
    } catch (err: any) {
      setError(err.message || "Failed to reactivate subscription. Please try again.")
    } finally {
      setIsLoading(false)
    }
  }

  const handleCopyLicense = async () => {
    const keyToCopy = displayLicenseKey || licenseKey
    if (!keyToCopy) {
      toast.error("License key not available")
      return
    }
    await navigator.clipboard.writeText(keyToCopy)
    setCopied(true)
    toast.success("License key copied to clipboard")
    setTimeout(() => setCopied(false), 2000)
  }

  const handleCreateAccount = async () => {
    // Validation
    if (!username) {
      setError("Please enter a username")
      return
    }
    
    // Validate username format
    const validation = validateUsername(username)
    if (!validation.valid) {
      setError(validation.error || "Invalid username")
      setUsernameError(validation.error || null)
      return
    }
    setUsernameError(null)
    if (!password) {
      setError("Please enter a password")
      return
    }
    if (password.length < 8) {
      setError("Password must be at least 8 characters")
      return
    }
    if (password !== confirmPassword) {
      setError("Passwords do not match")
      return
    }

    setError("")
    setIsLoading(true)

    try {
      // Get orderId from props, URL, or progress
      const urlParams = new URLSearchParams(window.location.search)
      const finalOrderId = orderId || urlParams.get("orderId") || ""

      if (!finalOrderId) {
        throw new Error("Order ID not found. Please restart the checkout process.")
      }

      // Save progress
      await fetch("/api/get-started/progress", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          email,
          step: "account",
          data: { email, username, orderId },
        }),
      })

      // Create account
      const response = await fetch("/api/get-started/create-account", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          email,
          username,
          password,
          orderId: finalOrderId,
        }),
      })

      if (!response.ok) {
        const data = await response.json()
        throw new Error(data.error || "Failed to create account")
      }

      const responseData = await response.json()

      // Track signup and checkout completion
      await trackConversion("signup", { username, email, orderId: finalOrderId })
      await trackConversion("checkout_completed", { 
        username, 
        email, 
        orderId: finalOrderId,
        planId: responseData.planId || "monthly"
      })

      // Clear progress
      await fetch("/api/get-started/progress", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          email,
          step: "success",
          data: {},
        }),
      })

      onComplete()
    } catch (err: any) {
      setError(err.message || "Failed to create account. Please try again.")
    } finally {
      setIsLoading(false)
    }
  }

  if (isCheckingUser) {
    return (
      <div className="space-y-6">
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin text-primary mx-auto" />
          <p className="mt-4 text-muted-foreground">Checking account status...</p>
        </div>
      </div>
    )
  }

  // Handle license redemption for signed-in users
  const handleRedeemLicense = async () => {
    if (!orderId) {
      setError("Order ID not found. Please restart the checkout process.")
      return
    }

    setIsRedeeming(true)
    setError("")

    try {
      const response = await fetch("/api/user/redeem-license", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ orderId, email }),
      })

      if (!response.ok) {
        const data = await response.json()
        throw new Error(data.error || "Failed to redeem license")
      }

      const data = await response.json()
      
      await trackConversion("license_redeemed", {
        email,
        orderId,
        isReactivation: data.isReactivation,
      })

      toast.success("License redeemed successfully! Redirecting to dashboard...")
      
      // Redirect to dashboard after a short delay
      setTimeout(() => {
        window.location.href = "/dashboard"
      }, 1500)
    } catch (err: any) {
      setError(err.message || "Failed to redeem license. Please try again.")
    } finally {
      setIsRedeeming(false)
    }
  }

  // If user already exists, show redemption UI
  if (isExistingUser) {
    if (isCheckingRedemption) {
      return (
        <div className="space-y-6">
          <div className="text-center">
            <Loader2 className="h-8 w-8 animate-spin text-primary mx-auto" />
            <p className="mt-4 text-muted-foreground">Checking license status...</p>
          </div>
        </div>
      )
    }

    // Show redemption UI if unredeemed license found
    if (redemptionData && redemptionData.hasUnredeemedLicense) {
      return (
        <div className="space-y-6">
          <div className="text-center">
            <h1 className="text-2xl font-bold">Redeem Your License</h1>
            <p className="mt-2 text-muted-foreground">
              You are currently signed in. Link your purchase to this account.
            </p>
          </div>

          {/* User Info Card */}
          <GlowCard variant="featured" className="p-6">
            <div className="text-center space-y-4">
              <div className="flex flex-col items-center gap-3">
                <div className="relative">
                  <img
                    src={redemptionData.user.profilePicture || "/icon.png"}
                    alt={redemptionData.user.username}
                    className="w-20 h-20 rounded-full border-2 border-primary/20 object-cover"
                  />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground mb-1">You are currently signed in as</p>
                  <h3 className="text-lg font-semibold">{redemptionData.user.username}</h3>
                  <p className="text-sm text-muted-foreground">{redemptionData.user.email}</p>
                </div>
              </div>
            </div>
          </GlowCard>

          {/* Redemption Prompt */}
          <GlowCard className="p-6">
            <div className="space-y-4">
              <div>
                <h3 className="text-lg font-semibold mb-2">Link License to This Account?</h3>
                <p className="text-sm text-muted-foreground">
                  Your purchase is ready to be linked to your account. This will:
                </p>
                <ul className="mt-2 space-y-1 text-sm text-muted-foreground list-disc list-inside">
                  <li>Activate your license key</li>
                  <li>Create your subscription</li>
                  <li>Send a confirmation email</li>
                  {redemptionData.hasOtherSubscriptions && (
                    <li className="text-yellow-500">Cancel your other active subscription(s)</li>
                  )}
                </ul>
              </div>

              {redemptionData.hasOtherSubscriptions && (
                <div className="p-3 rounded-lg bg-yellow-500/10 border border-yellow-500/20">
                  <p className="text-sm text-yellow-600 dark:text-yellow-400">
                    <strong>Note:</strong> You currently have {redemptionData.otherSubscriptions.length} active subscription(s). 
                    They will be cancelled when you redeem this license.
                  </p>
                </div>
              )}

              {error && (
                <div className="p-3 rounded-lg bg-destructive/10 border border-destructive/20">
                  <p className="text-sm text-destructive">{error}</p>
                </div>
              )}

              <div className="flex gap-3">
                <Button
                  onClick={handleRedeemLicense}
                  disabled={isRedeeming}
                  className="flex-1 gradient-purple hover:opacity-90 transition-opacity text-white font-medium"
                  size="lg"
                >
                  {isRedeeming ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Redeeming...
                    </>
                  ) : (
                    <>
                      <Check className="mr-2 h-4 w-4" />
                      Redeem on This Account
                    </>
                  )}
                </Button>
                <Button
                  onClick={() => window.location.href = "/dashboard"}
                  variant="outline"
                  size="lg"
                >
                  Cancel
                </Button>
              </div>
            </div>
          </GlowCard>
        </div>
      )
    }

    // Fallback to reactivation UI (for reactivations without orderId)
    return (
      <div className="space-y-6">
        <div className="text-center">
          <h1 className="text-2xl font-bold">Reactivate Your Subscription</h1>
          <p className="mt-2 text-muted-foreground">
            Link your new subscription to your existing account
          </p>
        </div>

        {/* Confirmation Message */}
        <GlowCard variant="featured" className="p-6">
          <div className="text-center space-y-4">
            <div className="w-16 h-16 rounded-full bg-primary/20 flex items-center justify-center mx-auto">
              <Check className="h-8 w-8 text-primary" />
            </div>
            <div>
              <h3 className="text-lg font-semibold mb-2">Subscription Ready to Link</h3>
              <p className="text-sm text-muted-foreground">
                Your subscription has been purchased. Click the button below to link it to your account.
                A receipt will be sent to your email.
              </p>
            </div>
          </div>
        </GlowCard>

        {/* Reactivate Button */}
        <div className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="email-display">Email</Label>
            <Input
              id="email-display"
              type="email"
              value={email}
              disabled
              className="bg-secondary/30 border-border text-muted-foreground"
            />
          </div>

          {error && (
            <div className="p-3 rounded-lg bg-destructive/10 border border-destructive/20">
              <p className="text-sm text-destructive">{error}</p>
            </div>
          )}

          <Button
            onClick={handleReactivate}
            disabled={isLoading || !orderId}
            className="w-full gradient-purple hover:opacity-90 transition-opacity text-white font-medium"
            size="lg"
          >
            {isLoading ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Reactivating...
              </>
            ) : (
              "Reactivate Subscription"
            )}
          </Button>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <div className="text-center">
        <h1 className="text-2xl font-bold">Create Your Account</h1>
        <p className="mt-2 text-muted-foreground">
          Set up your account to access Crystality
        </p>
      </div>

      {/* License Key Display */}
      {isLoadingLicense ? (
        <GlowCard variant="featured" className="p-4">
          <div className="flex items-center justify-center gap-2">
            <Loader2 className="h-4 w-4 animate-spin text-primary" />
            <p className="text-sm text-muted-foreground">Loading your license key...</p>
          </div>
        </GlowCard>
      ) : displayLicenseKey || licenseKey ? (
        <GlowCard variant="featured" className="p-4">
          <div className="flex items-center justify-between gap-4">
            <div className="flex-1 min-w-0">
              <p className="text-xs text-muted-foreground mb-1">Your License Key</p>
              <p className="font-mono text-sm break-all">{displayLicenseKey || licenseKey}</p>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={handleCopyLicense}
              className="flex-shrink-0 bg-transparent"
            >
              {copied ? (
                <Check className="h-4 w-4 text-green-500" />
              ) : (
                <Copy className="h-4 w-4" />
              )}
            </Button>
          </div>
          <p className="mt-2 text-xs text-muted-foreground">
            A copy has also been sent to {email}
          </p>
        </GlowCard>
      ) : (
        <GlowCard variant="featured" className="p-4 border-destructive/20">
          <div className="text-center space-y-2">
            <p className="text-sm text-destructive font-medium">License key not found</p>
            <p className="text-xs text-muted-foreground">
              Please check your email for the license key or contact support with your order ID: {orderId || "N/A"}
            </p>
          </div>
        </GlowCard>
      )}

      {/* Account Form */}
      <div className="space-y-4">
        <div className="space-y-2">
          <Label htmlFor="email-display">Email</Label>
          <Input
            id="email-display"
            type="email"
            value={email}
            disabled
            className="bg-secondary/30 border-border text-muted-foreground"
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="username">Username</Label>
          <Input
            id="username"
            type="text"
            placeholder="Choose a username"
            value={username}
            onChange={(e) => {
              const value = e.target.value
              // Filter out invalid characters (only allow alphanumeric)
              const filtered = value.replace(/[^a-zA-Z0-9]/g, "")
              setUsername(filtered)
              
              // Validate
              if (filtered.length > 0) {
                const validation = validateUsername(filtered)
                if (!validation.valid) {
                  setUsernameError(validation.error || null)
                } else {
                  setUsernameError(null)
                }
              } else {
                setUsernameError(null)
              }
              setError("") // Clear general error when typing
            }}
            className={usernameError ? "bg-secondary/50 border-destructive focus:border-destructive" : "bg-secondary/50 border-border focus:border-primary"}
            disabled={isLoading}
          />
          {usernameError && (
            <p className="text-xs text-destructive">{usernameError}</p>
          )}
          <p className="text-xs text-muted-foreground">
            Only letters and numbers allowed (no spaces or special characters)
          </p>
        </div>

        <div className="space-y-2">
          <Label htmlFor="password">Password</Label>
          <div className="relative">
            <Input
              id="password"
              type={showPassword ? "text" : "password"}
              placeholder="Create a password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="bg-secondary/50 border-border focus:border-primary pr-10"
              disabled={isLoading}
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
            >
              {showPassword ? (
                <EyeOff className="h-4 w-4" />
              ) : (
                <Eye className="h-4 w-4" />
              )}
            </button>
          </div>
        </div>

        <div className="space-y-2">
          <Label htmlFor="confirm-password">Confirm Password</Label>
          <Input
            id="confirm-password"
            type={showPassword ? "text" : "password"}
            placeholder="Confirm your password"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            className="bg-secondary/50 border-border focus:border-primary"
            disabled={isLoading}
          />
        </div>
      </div>

      {error && (
        <p className="text-sm text-destructive text-center">{error}</p>
      )}

      <Button
        className="w-full h-12 gradient-purple hover:opacity-90 transition-opacity text-white font-medium"
        onClick={handleCreateAccount}
        disabled={isLoading}
      >
        {isLoading ? (
          <>
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            Creating account...
          </>
        ) : (
          "Create Account"
        )}
      </Button>

      <p className="text-xs text-center text-muted-foreground">
        By creating an account, you agree to our{" "}
        <a href="/legal/terms" className="text-primary hover:underline">
          Terms of Service
        </a>{" "}
        and{" "}
        <a href="/legal/privacy" className="text-primary hover:underline">
          Privacy Policy
        </a>
      </p>
    </div>
  )
}
